import React, { useEffect, useState } from 'react';
import { Sparkles, RefreshCw, X, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import logger from '../utils/logger';

interface Briefing {
  id: number;
  briefing_date: string;
  frequency: 'daily' | 'weekly';
  summary: string;
  appointments_count: number;
  channels_sent: string;
  status: string;
  created_at: string;
  sent_at: string | null;
}

const STORAGE_KEY = 'na_briefing_dismissed_id';

const AgendaBriefingWidget: React.FC = () => {
  const { t } = useTranslation();
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(false);
  const [dismissed, setDismissed] = useState<number | null>(() => {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? parseInt(v) : null;
  });

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const resp: any = await apiService.get('/api/briefing/latest');
      if (resp.success) {
        setBriefing(resp.briefing || null);
      } else {
        logger.error('Failed to load briefing:', resp.message);
        setLoadError(true);
      }
    } catch (err) {
      logger.error('Failed to load briefing:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    setRunError(false);
    try {
      const resp: any = await apiService.post('/api/briefing/run', {});
      if (resp.success) {
        setDismissed(null);
        localStorage.removeItem(STORAGE_KEY);
        await load();
      } else {
        logger.error('Failed to run briefing:', resp.message);
        setRunError(true);
      }
    } catch (err) {
      logger.error('Failed to run briefing:', err);
      setRunError(true);
    } finally {
      setRunning(false);
    }
  };

  const dismiss = () => {
    if (briefing) {
      localStorage.setItem(STORAGE_KEY, String(briefing.id));
      setDismissed(briefing.id);
    }
  };

  if (loading) return null;

  if (loadError) {
    return (
      <div className="widget-v2">
        <div className="widget-v2-hd">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="widget-v2-icon"><Sparkles size={14} /></div>
            <span style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
              {t('briefing.widget.loadError')}
            </span>
          </div>
          <button onClick={load} title={t('briefing.widget.retry') as string}
            style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--ink-muted)', borderRadius: 'var(--radius)' }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (!briefing) return null;
  if (dismissed === briefing.id) return null;

  const briefingDate = new Date(briefing.briefing_date);
  const ageMs = Date.now() - briefingDate.getTime();
  const maxAge = briefing.frequency === 'weekly' ? 7 * 24 * 3600 * 1000 : 24 * 3600 * 1000;
  if (ageMs > maxAge) return null;

  return (
    <div className="widget-v2">
      <div className="widget-v2-hd">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div className="widget-v2-icon"><Sparkles size={14} /></div>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--ink)' }}>
            {briefing.frequency === 'weekly' ? t('briefing.widget.weeklyTitle') : t('briefing.widget.dailyTitle')}
          </h3>
          <span className="widget-v2-meta">
            <Calendar size={11} /> {t('briefing.widget.appointments', { count: briefing.appointments_count })}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button onClick={runNow} disabled={running}
            title={t('briefing.widget.refresh') as string}
            style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--ink-muted)', borderRadius: 'var(--radius)', opacity: running ? 0.5 : 1 }}>
            <RefreshCw size={14} className={running ? 'animate-spin' : ''} />
          </button>
          <button onClick={dismiss}
            title={t('common.close') as string}
            style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--ink-muted)', borderRadius: 'var(--radius)' }}>
            <X size={14} />
          </button>
        </div>
      </div>
      {runError && (
        <p style={{ fontSize: 12, color: 'var(--accent)', margin: '0 0 8px', padding: '0 16px' }}>
          {t('briefing.widget.runError')}
        </p>
      )}
      <pre style={{
        whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'inherit',
        lineHeight: 1.6, color: 'var(--ink)', margin: 0,
        padding: '0 16px 14px',
      }}>
        {briefing.summary}
      </pre>
    </div>
  );
};

export default AgendaBriefingWidget;
